// src/context/TenantContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';
import { tenantDataService } from '../services/tenantDataService';
import toast from 'react-hot-toast';

const TenantContext = createContext();

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export const TenantProvider = ({ children }) => {
  const [currentTenant, setCurrentTenant] = useState(null);
  const [tenantSettings, setTenantSettings] = useState(null);
  const [availableTenants, setAvailableTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currentUser, userProfile } = useAuth();

  // Load tenant information when user changes
  useEffect(() => {
    if (currentUser) {
      // Add timeout to prevent infinite loading
      const loadTimer = setTimeout(() => {
        loadUserTenants();
      }, 100);
      
      // Fallback timeout to prevent infinite loading
      const fallbackTimer = setTimeout(() => {
        console.log('⚠️ TenantContext: Loading timeout, setting loading to false');
        setLoading(false);
      }, 5000);
      
      return () => {
        clearTimeout(loadTimer);
        clearTimeout(fallbackTimer);
      };
    } else {
      setCurrentTenant(null);
      setTenantSettings(null);
      setAvailableTenants([]);
      setLoading(false);
    }
  }, [currentUser, userProfile]);

  // Load tenants available to the current user
  const loadUserTenants = async () => {
    if (!db || !currentUser) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Get custom claims from Firebase Auth token (force refresh to get latest claims)
      const tokenResult = await currentUser.getIdTokenResult(true);
      const customClaims = tokenResult.claims;
      
      console.log('🔍 TenantContext: Custom claims:', customClaims);

      // If user has clinicId in custom claims, load that tenant
      if (customClaims.clinicId) {
        console.log(`🏥 TenantContext: Loading tenant "${customClaims.clinicId}" from custom claims`);
        
        const tenantDoc = await getDoc(doc(db, 'tenants', customClaims.clinicId));
        if (tenantDoc.exists()) {
          const tenantData = { id: tenantDoc.id, ...tenantDoc.data() };
          setCurrentTenant(tenantData);
          setTenantSettings(tenantData.settings || {});
          setAvailableTenants([tenantData]);
          
          // Set tenant context in data service for data isolation
          tenantDataService.setTenantContext(tenantDoc.id);
          console.log(`✅ TenantContext: Loaded tenant "${tenantData.name}" successfully`);
        } else {
          console.error(`❌ TenantContext: Tenant document "${customClaims.clinicId}" not found`);
          toast.error('Tenant not found');
        }
      } else if (customClaims.role === 'super_admin' || customClaims.isSuperAdmin) {
        // Super admin can see all tenants
        console.log('🔑 TenantContext: Loading all tenants for super admin');
        
        const tenantsQuery = query(collection(db, 'tenants'));
        const tenantsSnapshot = await getDocs(tenantsQuery);
        let tenants = tenantsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Show available tenants but don't auto-select any
        setAvailableTenants(tenants);
        
        // Start with clean state - no clinic assigned
        setCurrentTenant(null);
        setTenantSettings(null);
        
        // Clear tenant context in data service for clean state
        tenantDataService.setTenantContext(null);
        console.log(`✅ TenantContext: Loaded ${tenants.length} tenants for super admin`);
      } else {
        // Fallback: load user data from users collection
        console.log('⚠️ TenantContext: No custom claims found, trying users collection fallback...');
        
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          console.log('📋 TenantContext: Found user data:', userData);
          
          if (userData.clinicId) {
            // Load the tenant data using clinicId from user document
            const tenantDoc = await getDoc(doc(db, 'tenants', userData.clinicId));
            if (tenantDoc.exists()) {
              const tenantData = { id: tenantDoc.id, ...tenantDoc.data() };
              setCurrentTenant(tenantData);
              setTenantSettings(tenantData.settings || {});
              setAvailableTenants([tenantData]);
              
              // Set tenant context in data service for data isolation
              tenantDataService.setTenantContext(tenantDoc.id);
              console.log(`✅ TenantContext: Loaded tenant from users fallback: "${tenantData.name}"`);
            } else {
              console.log('❌ TenantContext: Tenant document not found');
              toast.error('Clinic not found');
            }
          } else {
            console.log('❌ TenantContext: No clinicId in user document');
            toast.error('No clinic assigned to this user');
          }
        } else {
          console.log('❌ TenantContext: User document not found');
          toast.error('User profile not found');
        }
      }
    } catch (error) {
      console.error('Error loading user tenants:', error);
      toast.error('Failed to load clinic information');
    } finally {
      setLoading(false);
    }
  };

  // Switch to a different tenant (for super admins)
  const switchTenant = async (tenantId) => {
    if (!db) return;

    try {
      const tenantDoc = await getDoc(doc(db, 'tenants', tenantId));
      if (tenantDoc.exists()) {
        const tenantData = { id: tenantDoc.id, ...tenantDoc.data() };
        setCurrentTenant(tenantData);
        setTenantSettings(tenantData.settings || {});
        
        // Set tenant context in data service for data isolation
        tenantDataService.setTenantContext(tenantId);
        
        toast.success(`Switched to ${tenantData.name}`);
      }
    } catch (error) {
      console.error('Error switching tenant:', error);
      toast.error('Failed to switch clinic');
    }
  };

  // Get tenant-scoped collection path
  const getTenantCollection = (collectionName) => {
    if (!currentTenant) return collectionName;
    return `tenants/${currentTenant.id}/${collectionName}`;
  };

  // Check if user has permission for current tenant
  const hasPermission = (permission) => {
    if (!userProfile) {
      console.log('🔍 hasPermission: No userProfile');
      return false;
    }
    
    // Normalize role to lowercase for consistent checking
    const userRole = userProfile.role?.toLowerCase();
    console.log(`🔍 hasPermission: Checking permission "${permission}" for role "${userRole}"`);
    
    // Super admin has all permissions regardless of tenant
    if (userRole === 'super_admin') {
      console.log('✅ hasPermission: Super admin access granted');
      return true;
    }
    
    // Admin users can access admin functions even without tenant assignment (for initial setup)
    if (userRole === 'admin' && permission === 'admin') return true;
    
    // If no current tenant, only allow basic permissions for setup
    if (!currentTenant) {
      return userRole === 'admin' && ['admin', 'read', 'write'].includes(permission);
    }
    
    // Check if user belongs to current tenant
    if (userProfile.tenantId && userProfile.tenantId !== currentTenant.id) return false;
    
    // Role-based permissions within tenant
    const rolePermissions = {
      admin: ['read', 'write', 'delete', 'manage_staff', 'manage_settings', 'manage_billing', 'admin'],
      clinician: ['read', 'write', 'manage_notes'],
      staff: ['read', 'write']
    };
    
    return rolePermissions[userRole]?.includes(permission) || false;
  };

  const value = {
    currentTenant,
    tenantSettings,
    availableTenants,
    loading,
    switchTenant,
    getTenantCollection,
    hasPermission,
    loadUserTenants
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};
